import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

// How long the "Añadido" confirmation stays on the card
const ADDED_FEEDBACK_DURATION = 1800;

/**
 * Quick add — sizes strip on product cards.
 * A click on a size sets the variant on the hidden form input; the form then submits through the
 * theme's own product-form-component (which adds to the cart, updates the cart drawer and bubble).
 * This component only handles the visual state (selected size, "added" confirmation).
 *
 * Note: the hidden input belongs to the inner product-form-component (refs are scoped per component),
 * so it is looked up with querySelector instead of this.refs.
 *
 * @extends {Component}
 */
class QuickAddSizes extends Component {
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    // CartAddEvent / CartErrorEvent are dispatched on the inner product-form-component and bubble up here.
    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
    this.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#timeout) clearTimeout(this.#timeout);
  }

  /**
   * Runs (capture phase) when a size button is clicked, right before the form submits.
   * @param {Event} event
   */
  select(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button[data-variant-id]');

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantInput = this.querySelector('input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) return;

    variantInput.value = button.dataset.variantId || '';
    variantInput.disabled = false;

    for (const size of this.querySelectorAll('.quick-add__size')) {
      size.classList.toggle('is-selected', size === button);
    }

    this.dataset.state = 'adding';
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = (event) => {
    if (this.dataset.state !== 'adding') return;

    if (event.detail?.data?.didError) {
      this.#reset();
      return;
    }

    this.dataset.state = 'added';

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => this.#reset(), ADDED_FEEDBACK_DURATION);
  };

  #onCartError = () => {
    this.#reset();
  };

  #reset() {
    delete this.dataset.state;
    for (const size of this.querySelectorAll('.quick-add__size.is-selected')) {
      size.classList.remove('is-selected');
    }
  }
}

if (!customElements.get('quick-add-sizes')) {
  customElements.define('quick-add-sizes', QuickAddSizes);
}
